import * as React from 'react';
import { Unstable_NumberInput as BaseNumberInput, NumberInputProps } from '@mui/base/Unstable_NumberInput';
import { styled } from '@mui/system';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { handleEnterOrSpace } from '../../../components/staticComponents/StaticHtmlGenerator';

const NumberInput = React.forwardRef(function CustomNumberInput(
  props: NumberInputProps,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  return (
    <BaseNumberInput
      slots={{
        root: StyledInputRoot,
        input: StyledInput,
        incrementButton: StyledButton,
        decrementButton: StyledButton,
      }}
      slotProps={{
        incrementButton: {
          children: <AddIcon fontSize="small" />,
          className: 'increment',
          tabIndex: 0,
          'aria-label': 'Increase value',
          onKeyDown: (e) => handleEnterOrSpace(e, () => e.currentTarget.click()),
        },
        decrementButton: {
          children: <RemoveIcon fontSize="small" />,
          tabIndex: 0,
          'aria-label': 'Decrease value',
          onKeyDown: (e) => handleEnterOrSpace(e, () => e.currentTarget.click()),
        },
      }}
      {...props}
      ref={ref}
    />
  );
});

export default NumberInput;

const grey = {
  50: '#F3F6F9',
  100: '#E5EAF2',
  200: '#DAE2ED',
  300: '#C7D0DD',
  400: '#B0B8C4',
  500: '#9DA8B7',
  600: '#6B7A90',
  700: '#434D5B',
  800: '#303740',
  900: '#1C2025',
};

const StyledInputRoot = styled('div')(
  ({ theme }) => `
  font-weight: 400;
  color: ${theme.palette.mode === 'dark' ? grey[300] : grey[500]};
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
`
);

const StyledInput = styled('input')(
  ({ theme }) => `
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.375;
  color: ${theme.palette.mode === 'dark' ? grey[300] : grey[900]};
  background: ${theme.palette.mode === 'dark' ? grey[900] : '#fff'};
  border: 1px solid ${theme.palette.mode === 'dark' ? grey[700] : grey[200]};
  box-shadow: 0 2px 4px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0, 0.5)' : 'rgba(0,0,0, 0.05)'};
  margin: 0 4px;
  padding: 0px 5px;
  border-radius: 5px;
  outline: 0;
  min-width: 0;
  width: 2rem;
  text-align: center;
  height: 20px;

  &:hover {
    border-color:  #E9D8FD;
  }

  &:focus {
    border-color: #E9D8FD;
    box-shadow: 0 0 0 3px #E9D8FD;
  }

  &:focus-visible {
    outline: 0;
  }
`
);

const StyledButton = styled('button')(
  ({ theme }) => `
  font-size: 0.875rem;
  box-sizing: border-box;
  line-height: 1.5;
  border: 1px solid;
  border-color: ${theme.palette.mode === 'dark' ? grey[800] : grey[200]};
  background: #ffffff;
  color: ${theme.palette.mode === 'dark' ? grey[200] : grey[900]};
  width: px;
  height: 20px;
  margin-left: 0px;
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 120ms;
  padding: 5px;
  border-radius: 5px;

  &:hover {
    cursor: pointer;
    background: #3C82F6;
    border-color: #3C82F6;
    color: ${grey[50]};
  }

  &:focus-visible {
    outline: 1px solid #3C82F6;
  }

  &.increment {
    order: 1;
  }
`
);
